import { currentSessionIdentifiers } from "./app-mode.js";

const STORAGE_KEY = currentSessionIdentifiers.storageKey;
const CHANNEL_NAME = currentSessionIdentifiers.channelName;
const CLIENT_INSTANCE_TOKEN = /^[A-Za-z0-9_-]{32,128}$/u;

export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface CoordinationMessage {
  type: "instance-request" | "instance-response" | "logout";
  requestId?: string;
  token?: string;
}

const browserStorage = (): SessionStorageLike | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const openChannel = (): BroadcastChannel | null => {
  if (
    typeof window === "undefined" ||
    typeof window.BroadcastChannel !== "function"
  )
    return null;
  return new window.BroadcastChannel(CHANNEL_NAME);
};

const message = (value: unknown): CoordinationMessage | null => {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Partial<CoordinationMessage>;
  if (
    candidate.type !== "instance-request" &&
    candidate.type !== "instance-response" &&
    candidate.type !== "logout"
  )
    return null;
  return candidate as CoordinationMessage;
};

export const isClientInstanceToken = (value: string): boolean =>
  CLIENT_INSTANCE_TOKEN.test(value);

export const currentClientInstanceToken = (
  storage: SessionStorageLike | null = browserStorage(),
): string | null => {
  const token = storage?.getItem(STORAGE_KEY) ?? null;
  if (token === null || !isClientInstanceToken(token)) {
    if (token !== null) storage?.removeItem(STORAGE_KEY);
    return null;
  }
  return token;
};

export const storeClientInstanceToken = (
  token: string,
  storage: SessionStorageLike | null = browserStorage(),
): boolean => {
  if (storage === null || !isClientInstanceToken(token)) return false;
  storage.setItem(STORAGE_KEY, token);
  return true;
};

export const ensureClientInstanceToken = (
  storage: SessionStorageLike | null = browserStorage(),
  createToken: () => string = () => globalThis.crypto.randomUUID(),
): string => {
  const existing = currentClientInstanceToken(storage);
  if (existing !== null) return existing;
  const token = createToken();
  if (!storeClientInstanceToken(token, storage))
    throw new Error(
      "Il browser non permette una sessione sicura. Abilitare sessionStorage e riprovare.",
    );
  return token;
};

export const clearClientInstanceToken = (
  storage: SessionStorageLike | null = browserStorage(),
): void => storage?.removeItem(STORAGE_KEY);

export const clientInstanceRequestHeaders = (
  storage: SessionStorageLike | null = browserStorage(),
): Record<string, string> => {
  const token = currentClientInstanceToken(storage);
  return token === null ? {} : { "x-cem-client-instance": token };
};

export const startSessionCoordination = (
  onLogout: () => void,
): (() => void) => {
  const channel = openChannel();
  if (channel === null) return () => undefined;
  const receive = (event: MessageEvent<unknown>): void => {
    const received = message(event.data);
    if (received?.type === "instance-request") {
      const token = currentClientInstanceToken();
      if (token !== null && received.requestId !== undefined) {
        channel.postMessage({
          type: "instance-response",
          requestId: received.requestId,
          token,
        } satisfies CoordinationMessage);
      }
    } else if (received?.type === "logout") {
      clearClientInstanceToken();
      onLogout();
    }
  };
  channel.addEventListener("message", receive);
  return () => {
    channel.removeEventListener("message", receive);
    channel.close();
  };
};

export const adoptClientInstanceFromOpenPage = async (
  timeoutMilliseconds = 250,
): Promise<boolean> => {
  if (currentClientInstanceToken() !== null) return true;
  const channel = openChannel();
  if (channel === null) return false;
  const requestId = globalThis.crypto.randomUUID();
  return new Promise((resolve) => {
    let completed = false;
    const finish = (adopted: boolean): void => {
      if (completed) return;
      completed = true;
      window.clearTimeout(timer);
      channel.removeEventListener("message", receive);
      channel.close();
      resolve(adopted);
    };
    const receive = (event: MessageEvent<unknown>): void => {
      const received = message(event.data);
      if (
        received?.type === "instance-response" &&
        received.requestId === requestId &&
        received.token !== undefined &&
        storeClientInstanceToken(received.token)
      )
        finish(true);
    };
    const timer = window.setTimeout(() => finish(false), timeoutMilliseconds);
    channel.addEventListener("message", receive);
    channel.postMessage({
      type: "instance-request",
      requestId,
    } satisfies CoordinationMessage);
  });
};

export const broadcastSessionLogout = (): void => {
  const channel = openChannel();
  channel?.postMessage({ type: "logout" } satisfies CoordinationMessage);
  channel?.close();
};
